// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.
/* eslint-disable no-console */

import { handleSubmitError } from '@/components/notification/defaults';
import { Formik, Form as FormikForm, getIn, setIn } from 'formik';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import * as yup from 'yup';

import { isFormGroupField, splitFormGroupFieldName } from './formUtils';
import { FormContext } from './FormContext';
// These is here to avoid breaking code that expected
// Form to export FormContext and FormGroup
export { FormContext };
export * from './FormGroup';

const FORM_DATA_PREFIX = 'form_data_';

/**
 * @property {string} [id] - OPCIONAL: ID do formulário. Se não for informado, será gerado um ID aleatório. RECOMENDADO PARA USAR O LOCALSTORAGE.
 * @property {boolean} [disableLocalStorage] - DEFAULT FALSE. Se for true, o estado do formulário será salvo no localStorage.
 * @property {function(values: object, submitProps: any): Promise<void>} handleSubmit - DEFAULT: (values, submitProps) => Promise.resolve(). Função chamada quando o formulário é enviado. Recebe os valores serializados do formulário e as props de submit. Deve retornar uma Promise.
 * @property {boolean} [disableSuccessToast] - DEFAULT FALSE. Se for true, não será exibido o toast de sucesso ao enviar o formulário.
 */

export class Form extends React.Component {
  _validate = () => this.state.customValidation;

  constructor(props) {
    super(props);
    this.formId =
      props.id || `form_${Math.random().toString(36).substring(2, 11)}`;

    const {
      initialValues: initialValuesLocalStorage,
      currentStep: currentStepLocalStorage,
    } = this.loadFromLocalStorage(props);

    if (
      this.props?.initialStep != null &&
      !Number.isInteger(this.props?.initialStep)
    ) {
      throw new Error('Initial step must be an integer');
    }

    this.state = {
      customValidation: {},
      initialValues: {},
      initialValuesLocalStorage: initialValuesLocalStorage,
      validationSchema: {},
      serializeFuncList: {},
      deserializeFuncList: {},
      formLoaded: false,
      callbackFuncList: {},
      lastValues: null,
      currentStep: currentStepLocalStorage ?? this.props?.initialStep ?? 0,
      fieldStepMap: {
        // name: stepIndex
      },
      stepConfig: [
        // {
        //   index: stepIndex,
        //   label: stepLabel,
        //   Icon?: StepIcon
        // }
      ],
    };
  }

  loadFromLocalStorage = (props) => {
    let initialValues = null;
    let currentStep;

    if (!props.disableLocalStorage && typeof window !== 'undefined') {
      const savedData = localStorage.getItem(
        `${FORM_DATA_PREFIX}${this.formId}`,
      );
      const savedStep = localStorage.getItem(
        `${FORM_DATA_PREFIX}${this.formId}_currentStep`,
      );
      if (savedData) {
        try {
          initialValues = JSON.parse(savedData);
          currentStep = JSON.parse(savedStep);
        } catch (error) {
          console.error('Error loading form data from localStorage:', error);
        }
      }
    }

    return { initialValues, currentStep };
  };

  _saveToLocalStorageImmediate = (values) => {
    if (this.props.disableLocalStorage) return;
    if (!values || Object.keys(values).length === 0) return;
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/auth')
    )
      return;
    const cleanValues = { ...values };

    const sensitiveFields = ['key', 'password', 'senha', 'chave', 'token'];
    Object.keys(values).forEach((key) => {
      if (sensitiveFields.some((field) => key.toLowerCase().includes(field))) {
        delete cleanValues[key];
      }
    });

    try {
      localStorage.setItem(
        `${FORM_DATA_PREFIX}${this.formId}`,
        JSON.stringify(this.serializeValues(cleanValues)),
      );
      localStorage.setItem(
        `${FORM_DATA_PREFIX}${this.formId}_currentStep`,
        JSON.stringify(this.state.currentStep),
      );
    } catch (error) {
      console.error('Error saving form data to localStorage:', error);
    }
  };

  saveToLocalStorage = (values) => {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => {
      this._saveToLocalStorageImmediate(values);
    }, 300);
  };

  componentWillUnmount() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
  }

  clearLocalStorage = () => {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    if (this.props.disableLocalStorage) return;

    try {
      localStorage.removeItem(`${FORM_DATA_PREFIX}${this.formId}`);
    } catch (error) {
      console.error('Error clearing form data from localStorage:', error);
    }
  };

  getFormContext = (formikProps) => {
    return {
      values: formikProps.values,
      errors: formikProps.errors,
      touched: formikProps.touched,
      initialValues: formikProps.initialValues,
      validate: formikProps.validateForm,
      isSubmitting: formikProps.isSubmitting,
      isValid: formikProps.isValid,
      submitForm: formikProps.submitForm,
      setFieldValue: formikProps.setFieldValue,
      setFieldTouched: (name, isTouched, shouldValidate) => {
        formikProps.setFieldTouched(name, isTouched, shouldValidate);
        this.setCustomErrorState(name, null, () => {
          formikProps.validateForm();
        });
      },
      setCustomError: (name, error) => {
        this.setCustomErrorState(name, error, () => {
          formikProps.validateForm();
        });
      },
      setInitialValue: this.setInitialValue,
      setCallbackFunc: this.setCallbackFunc,
      setSerializeFunc: this.setSerializeFunc,
      setDeserializeFunc: this.setDeserializeFunc,
      currentStep: this.state.currentStep,
      fieldStepMap: this.state.fieldStepMap,
      stepConfig: this.state.stepConfig,
      setCurrentStep: this.setCurrentStep,
      setFieldStepMap: this.setFieldStepMap,
      setStepConfig: this.setStepConfig,
      setValidationSchema: this.setValidationSchema,
    };
  };

  setCustomErrorState = (name, error, callback) => {
    const isFormGroup = isFormGroupField(name);
    let fieldIndex;
    let fieldName;
    if (isFormGroup) {
      const splittedName = splitFormGroupFieldName(name);
      name = splittedName.groupName;
      fieldIndex = splittedName.fieldIndex;
      fieldName = splittedName.fieldName;
    }
    this.setState((state) => {
      if (error == null && state.customValidation[name] != null) {
        if (!isFormGroup) {
          delete state.customValidation[name];
        } else {
          delete state.customValidation[name][fieldIndex][fieldName];
          if (
            state.customValidation[name].every((group) => {
              return group == null || Object.entries(group).length === 0;
            })
          ) {
            delete state.customValidation[name];
          }
        }
      } else if (error != null) {
        state.customValidation[name] = error;
      }
      state.formLoaded = true;
      return state;
    }, callback);
  };

  setInitialValue = (name, value) => {
    this.setState((state) => {
      if (value == null) {
        delete state.initialValues[name];
      }
      state.initialValues[name] = value;
      state.formLoaded = true;
      return state;
    });
  };

  setValidationSchema = (name, schema) => {
    this.setState((state) => {
      if (schema == null) {
        delete state.validationSchema[name];
      }
      state.validationSchema[name] = schema;
      state.formLoaded = true;
      return state;
    });
  };

  setSerializeFunc = (name, serializeFunc) => {
    this.setState((state) => {
      if (serializeFunc == null) {
        delete state.serializeFuncList[name];
      }
      state.serializeFuncList[name] = serializeFunc;
      state.formLoaded = true;
      return state;
    });
  };

  setDeserializeFunc = (name, deserializeFunc) => {
    this.setState((state) => {
      if (deserializeFunc == null) {
        delete state.deserializeFuncList[name];
      }
      state.deserializeFuncList[name] = deserializeFunc;
      state.formLoaded = true;
      return state;
    });
  };

  setCurrentStep = (nextStep) => {
    this.setState((state) => {
      if (typeof nextStep === 'function') {
        state.currentStep = nextStep();
        return state;
      }
      state.currentStep = nextStep;
      return state;
    });
  };

  setFieldStepMap = (fieldName, stepIndex) => {
    this.setState((state) => {
      const prevFieldStepMap = state.fieldStepMap;
      state.fieldStepMap = { ...prevFieldStepMap, [fieldName]: stepIndex };
      return state;
    });
  };

  setStepConfig = (stepIndex, stepLabel, StepIcon) => {
    if (!Number.isInteger(stepIndex)) {
      throw new Error('Step index must be an integer.');
    }
    this.setState((state) => {
      const prevStepConfig = state.stepConfig;
      const configWithRemovedIndex = prevStepConfig.filter((element) => {
        return element.index !== stepIndex;
      });
      state.stepConfig = [
        ...configWithRemovedIndex,
        { index: stepIndex, label: stepLabel, Icon: StepIcon },
      ];
      return state;
    });
  };

  serializeValues = (values, removeUnset = false) => {
    const orderedTranformFunction = Object.entries(
      this.state.serializeFuncList,
    );
    orderedTranformFunction.sort(
      (a, b) => b[0].split('.').length - a[0].split('.').length,
    );
    let valuesTransformed = values;
    orderedTranformFunction.forEach(([name, func]) => {
      const currentValue = getIn(valuesTransformed, name);
      if (func && currentValue != undefined) {
        const returnValue = func(currentValue);
        if (returnValue == undefined && removeUnset) {
          delete valuesTransformed[name];
        } else {
          valuesTransformed = setIn(valuesTransformed, name, returnValue);
        }
      }
    });
    return valuesTransformed;
  };

  serializeValuesForSubmit = (values) => {
    return this.serializeValues(values, true);
  };

  deserializeValues = (values) => {
    if (!this.state.formLoaded) return null;

    const orderedTranformFunction = Object.entries(
      this.state.deserializeFuncList,
    );
    orderedTranformFunction.sort(
      (a, b) => a[0].split('.').length - b[0].split('.').length,
    );
    let valuesTransformed = values;
    orderedTranformFunction.forEach(([name, func]) => {
      const currentValue = getIn(valuesTransformed, name);
      if (func && currentValue != undefined) {
        const returnValue = func(currentValue);
        if (returnValue == undefined) {
          delete valuesTransformed[name];
        } else {
          valuesTransformed = setIn(valuesTransformed, name, returnValue);
        }
      }
    });
    return valuesTransformed;
  };

  setCallbackFunc = (listenerField, dependentField, callbackFunc) => {
    this.setState((state) => {
      if (!state.callbackFuncList[dependentField])
        state.callbackFuncList[dependentField] = {};
      state.callbackFuncList[dependentField][listenerField] = callbackFunc;
      return state;
    });
  };

  triggerCallbackFunc = (values, setValue) => {
    Object.entries(this.state.callbackFuncList).forEach(
      ([dependentField, callbackFuncList]) => {
        if (
          getIn(this._lastValues, dependentField) !=
          getIn(values, dependentField)
        ) {
          Object.entries(callbackFuncList).forEach(
            ([listenerField, callbackFunc]) => {
              let groupValues = undefined;
              if (listenerField.includes('.')) {
                const groupPrefix = listenerField
                  .split('.')
                  .slice(0, -1)
                  .join('.');
                groupValues = getIn(values, groupPrefix);
              }
              const returnValue = callbackFunc(
                getIn(values, dependentField),
                getIn(values, listenerField),
                values,
                groupValues,
              );
              if (returnValue != undefined) {
                setValue(listenerField, returnValue);
              }
            },
          );
        }
      },
    );
  };

  render() {
    const submit = (values, formikBag) => {
      const submitProps = values['__UNSET__submit_props'];
      delete values['__UNSET__submit_props'];

      return this.props
        .handleSubmit(this.serializeValuesForSubmit(values), submitProps)
        .then(() => {
          if (!this.props.disableLocalStorage) {
            this.clearLocalStorage();
          }

          if (!this.props.disableSuccessToast)
            enqueueSnackbar('Sucesso!', { variant: 'success' });
        })
        .catch((error) => {
          handleSubmitError(
            error,
            (name, error) => {
              this.setCustomErrorState(name, error, () => {
                formikBag.validateForm();
              });
            },
            this.setCurrentStep,
            this.state.fieldStepMap,
          );
        })
        .finally(() => {
          formikBag.setFieldValue('__UNSET__submit_props', undefined);
        });
    };

    return (
      <Formik
        onSubmit={submit}
        initialValues={
          this.deserializeValues(this.state.initialValuesLocalStorage) ??
          this.state.initialValues
        }
        validationSchema={yup.object().shape(this.state.validationSchema)}
        validateOnMount
        validateOnChange={this.props.validateOnChange ?? true}
        validate={this._validate}
        enableReinitialize
      >
        {(formikProps) => {
          const _valJson = JSON.stringify(formikProps.values);
          React.useEffect(() => {
            if (!this.props.disableLocalStorage) {
              this.saveToLocalStorage(formikProps.values);
            }
          }, [
            _valJson,
            this.props.disableLocalStorage,
            this.state.currentStep,
          ]);

          React.useEffect(() => {
            this.triggerCallbackFunc(
              formikProps.values,
              formikProps.setFieldValue,
            );
            this._lastValues = formikProps.values;
          }, [_valJson]);

          return (
            <FormikForm>
              <FormContext.Provider value={this.getFormContext(formikProps)}>
                {this.props.children}
              </FormContext.Provider>
            </FormikForm>
          );
        }}
      </Formik>
    );
  }
}

export function cleanLocalStorageValues() {
  if (typeof window !== 'undefined') {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(FORM_DATA_PREFIX)) {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error removing ${key} from localStorage:`, error);
        }
      }
    });
  }
}
